import { HttpClient } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { combineLatest } from 'rxjs';
import { remove as _remove } from 'lodash';
import { filter } from 'rxjs/operators';
import { GoogleAnalyticsService } from 'src/app/core/services/google-tag-manager.service';
import { PGHOktaService } from 'src/app/core/services/pghokta.service';
import { ISelectedContract, LoggedInUserSettings } from 'src/app/core/services/settings/logged-in-user-settings';
import { UserClaims } from 'src/app/model/okta/userClaims';
import { CHAT, CHATGROUP } from '../../../../core/constants/feature-key.constants';
import { EssDeviceDto } from 'src/app/shared/device-auto-complete-mfe/ess-device.dto';
import { DeviceCardTemplate } from 'src/app/shared/device/device-card/device-card.component';
import { IDeviceCardDetails } from 'src/app/shared/device/device-card/device-card.component';
import { IPromoSlot } from 'src/app/model/promos/IPromoSlot';
import { ADPResourceService } from 'digital-core';
import { DeviceInventoryComponent } from 'src/app/shared/device/device-inventory/device-inventory.component';
import { GTMEvents } from '../../../../model/google-tag-manager/api-status';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-device-inventory-v3',
  templateUrl: './device-inventory-v3.component.html',
  styleUrls: ['./device-inventory-v3.component.scss'],
})
export class DeviceInventoryComponentV3 implements OnInit, OnDestroy {
  @ViewChild('deviceInventoryComponent') deviceInventoryComponent: DeviceInventoryComponent;

  public promoSlot: IPromoSlot;
  public accessToken: string = null;
  public hasRegisteredDevices: boolean = true;
  public deviceInventoryMfeBaseUrl: string;
  public autocompleteMfeBaseUrl: string;
  public deviceAutocompleteContentInputs: any = {};
  public isTechServicesChatEnabled: boolean = false;
  public isPhonePlanSupported: boolean = false;
  public programCode: string;
  private selectedContract?: ISelectedContract;
  private cancelledOrExpiredHasFullAccess: boolean = false;

  public isAutoCompleteHidden:boolean = false;

  // GA variables
  private userEmail: string;
  private userType: string;
  private usersOnContract: number;

  // test temp
  public deviceDetails: IDeviceCardDetails;
  public phoneInventoryList: IDeviceCardDetails[];
  public deviceInventoryList: IDeviceCardDetails[] = [];
  public deviceCardTemplate: DeviceCardTemplate = DeviceCardTemplate.home_device;

  public isSPOEEnabled: boolean = false;

  private googleAnalyticsService = inject(GoogleAnalyticsService);

  get isChatDisabled() {
    return (
      (this.selectedContract?.isCancelled ?? false) ||
      ((this.selectedContract?.isExpired ?? false) && !this.cancelledOrExpiredHasFullAccess)
    );
  }

  constructor(
    public httpClient: HttpClient,
    private loggedInUserSettings: LoggedInUserSettings,
    private pghOktaService: PGHOktaService,
    public resourceService: ADPResourceService,
    public activatedRoute: ActivatedRoute
  ) {}

  async ngOnInit() {
    await this.loadComponents();
    this.googleAnalyticsService.emitDeviceInventoryEvent(GTMEvents.DeviceInventoryView, undefined, undefined, undefined, undefined);

    this.isSPOEEnabled = this.resourceService.isPortalSettingTrue('Global_SPOE_Enabled');

    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['headerKey'] != null) {
        this.isAutoCompleteHidden = this.resourceService.getPortaLabelValue(params['headerKey'])==="device_autocomplete_hidden" ;
      }
    });

    window.scrollTo(0, 0);
  }

  async loadComponents() {
    this.accessToken = this.pghOktaService.getAccessToken();

    const joined$ = combineLatest([this.loggedInUserSettings.$selectedContract, this.loggedInUserSettings.$userClaims]).pipe(
      filter(([a, b]) => a !== null && b !== null)
    );

    joined$.subscribe(([contract, userClaims]: [ISelectedContract, UserClaims]) => {
      this.selectedContract = contract;
      const isUserPrimary = this.googleAnalyticsService.isUserPrimary(contract, userClaims);
      this.userEmail = userClaims.email;
      this.userType = isUserPrimary ? 'Primary' : 'Secondary';
      this.usersOnContract = contract.members.length;

      this.isTechServicesChatEnabled =
        this.loggedInUserSettings.isFeatureEnabled(this.selectedContract.dealerId, CHATGROUP) &&
        this.loggedInUserSettings.isFeatureEnabled(this.selectedContract.dealerId, CHAT);
    });
    this.promoSlot = {
      heading: 'Devices_Promo_Heading',
      promoText: 'Devices_Promo_Text',
      secondaryHeader: 'Devices_Promo_Secondary_Header',
      secondaryText: 'Devices_Promo_Secondary_Text',
      imageKey: 'Global_Devices_Promo_ImageKey',
      callToActionLabel: 'Devices_Promo_CallToAction_Button_Label',
      isPromoAppType: this.resourceService.isPortalSettingTrue('Devices_Promo_IsPromoAppType'),
    };
    window.addEventListener('message', this.scrollToPosition);
    this.cancelledOrExpiredHasFullAccess = this.resourceService.isPortalSettingTrue('Global_CancelledOrExpiredContract_HasFullAccess');
  }

  public scrollToPosition(event: any) {
    if (event.data.action === 'deviceFormLoaded') {
      const element = document.querySelector('.device-inventory-container');
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  public deviceSelected(event: EssDeviceDto) {
    this.deviceInventoryComponent.deviceSelected(event);
  }

  public noDevice(event?: any) {
    this.deviceInventoryComponent.noDevice();
  }

  public handleAddDevice(event?: any){
    this.isAutoCompleteHidden = true;
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.scrollToPosition);
  }
}
